import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { getIsUserSignedIn, getSignInState, SignedInState } from "@gamedev-web/gamedev-sdk-core";
import { Programs } from "../models/programsConfig";
import { IdXboxApplicationStep, NdaStatus } from "../models/status";
import { AaaGameInformationSubFormsId } from "../pages/AaaGameInformationPage/AaaGameInformationPageConstants";
import { GameInformationSubFormsId } from "../pages/GameInformationPage/GameInformationPageConstants";
import { IdXboxIntroSubFormsId } from "../pages/IdXboxIntroPage/IdXboxIntroPageConstants";
import { PitchInformationSubFormsId } from "../pages/PitchInformationPage/PitchInformationPageConstants";
import { RouteNames } from "../partnerConfig/routeNames";
import { OnboardingUrls, Paths } from "../partnerConfig/routes";
import { gameConceptActions } from "../redux/actions/gameConceptActions";
import { statusActions } from "../redux/actions/statusActions";
import {
  getAaaApplicationStep,
  getCurrentProgram,
  getHasAtLeastOneAcceptedApplication,
  getIdXboxApplicationStep,
  getIsAaaOnboardingHubEnabled,
  getIsFastTrackAccount,
  getIsGdkReceived,
  getIsInitialCrmFetchDone,
  getNdaStatus,
} from "../redux/selectors";
import { getIsAaaUser } from "../redux/selectors/capabilitiesSelectors";
import { GameConceptIdSearchParam, PitchConceptIdSearchParam } from "./dataFetchingHelpers";
import { useIsFastTrackAccountAndComplete } from "./idXboxStatusHelpers";

export const getIdXboxIntroFormUrl = (subFormId: IdXboxIntroSubFormsId): string =>
  OnboardingUrls[RouteNames.IdXboxIntro].replace(Paths.PageIdPlaceholder, subFormId);

export const getGameInformationFormUrl = (subFormId: GameInformationSubFormsId): string =>
  OnboardingUrls[RouteNames.IdXboxGameInfoForm].replace(Paths.PageIdPlaceholder, subFormId);

export const getPitchInformationFormUrl = (subFormId: PitchInformationSubFormsId): string =>
  OnboardingUrls[RouteNames.PitchInformation].replace(Paths.PageIdPlaceholder, subFormId);

export const getAaaGameInformationFormUrl = (subFormId: AaaGameInformationSubFormsId): string =>
  OnboardingUrls[RouteNames.AaaGameInformation].replace(Paths.PageIdPlaceholder, subFormId);

export const getDashboardUrl = (
  programDashboardRouteName: RouteNames,
  gameConceptId?: string
): string =>
  gameConceptId
    ? `${OnboardingUrls[programDashboardRouteName]}?${GameConceptIdSearchParam}=${gameConceptId}`
    : OnboardingUrls[programDashboardRouteName];

export const getPitchDashboardUrl = (pitchConceptId?: string): string =>
  pitchConceptId
    ? `${OnboardingUrls[RouteNames.PitchHome]}?${PitchConceptIdSearchParam}=${pitchConceptId}`
    : OnboardingUrls[RouteNames.PitchHome];

export const useOnIdXboxLoad = (routeName: RouteNames) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const currentApplicationStep = useSelector(getIdXboxApplicationStep);
  const isSignedIn = useSelector(getIsUserSignedIn);
  const atLeastOneAcceptedApplication = useSelector(getHasAtLeastOneAcceptedApplication);
  const isGdkReceived = useSelector(getIsGdkReceived);
  const signedInState = useSelector(getSignInState);
  const isFastTrackAccountAndComplete = useIsFastTrackAccountAndComplete();
  const isFastTrackAccount = useSelector(getIsFastTrackAccount);
  const isInitialCrmFetchDone = useSelector(getIsInitialCrmFetchDone);
  const ndaStatus = useSelector(getNdaStatus);

  useEffect(() => {
    // TODO: anmarc ; investigate moving these redirection rules to sagas if possible
    if (signedInState === SignedInState.SignedOut) {
      history.replace(OnboardingUrls[RouteNames.IdXboxWelcome]);
      return;
    }

    if (
      isInitialCrmFetchDone &&
      routeName === RouteNames.IdXboxGameInfoDashboard &&
      ndaStatus !== NdaStatus.Signed
    ) {
      history.replace(OnboardingUrls[RouteNames.IdXboxWelcome]);
      return;
    }

    dispatch(statusActions.updateStep(currentApplicationStep, Programs.IdXbox));
  }, [
    isSignedIn,
    history,
    currentApplicationStep,
    dispatch,
    routeName,
    atLeastOneAcceptedApplication,
    signedInState,
    isFastTrackAccountAndComplete,
    isFastTrackAccount,
    isInitialCrmFetchDone,
    isGdkReceived,
    ndaStatus,
  ]);

  useEffect(() => {
    if (routeName === RouteNames.IdXboxWelcome) {
      dispatch(gameConceptActions.clearSelectedIdXboxGameConcept());
    }
  }, [routeName, dispatch, currentApplicationStep]);
};

export const useOnAaaLoad = (routeName: RouteNames) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const currentApplicationStep = useSelector(getAaaApplicationStep);
  const signedInState = useSelector(getSignInState);
  const currentProgram = useSelector(getCurrentProgram);
  const isAaaOnboardingHubEnabled = useSelector(getIsAaaOnboardingHubEnabled);
  const isAaaUser = useSelector(getIsAaaUser);
  const isInitialCrmFetchDone = useSelector(getIsInitialCrmFetchDone);

  useEffect(() => {
    if (!isInitialCrmFetchDone) {
      return;
    }

    if (
      routeName === RouteNames.AaaWelcome ||
      routeName === RouteNames.AaaDashboard ||
      routeName === RouteNames.AaaGameInformation ||
      routeName === RouteNames.AaaFormPreview
    ) {
      if (signedInState === SignedInState.SignedOut || isAaaUser === false) {
        history.replace(OnboardingUrls[RouteNames.Landing]);
        return;
      }

      if (routeName === RouteNames.AaaDashboard || routeName === RouteNames.AaaFormPreview) {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        if (
          !urlParams.has(GameConceptIdSearchParam) ||
          urlParams.get(GameConceptIdSearchParam) === ""
        ) {
          history.replace(OnboardingUrls[RouteNames.AaaWelcome]);
          return;
        }
      }
    }
    dispatch(statusActions.updateStep(currentApplicationStep, Programs.Aaa));
  }, [
    isAaaUser,
    currentApplicationStep,
    currentProgram,
    dispatch,
    history,
    routeName,
    signedInState,
    isAaaOnboardingHubEnabled,
    isInitialCrmFetchDone,
  ]);

  useEffect(() => {
    if (routeName === RouteNames.AaaWelcome) {
      dispatch(gameConceptActions.clearSelectedAaaGameConcept());
    }
  }, [routeName, dispatch, currentApplicationStep]);
};

export const useIsIdXboxWelcomePageEnabled = () => {
  const step = useSelector(getIdXboxApplicationStep);
  const isFastTrackAccountAndComplete = useIsFastTrackAccountAndComplete();
  return step === IdXboxApplicationStep.ApplicationComplete || isFastTrackAccountAndComplete;
};
